// src/components/RouteErrorBoundary.jsx
//
// Lightweight error boundary for the routing subtree. Catches render
// errors inside a single route so one broken page doesn't whitescreen
// the whole app — the sidebar, navigation, and command palette stay
// usable, and a "Try again" button remounts the route in place.
//
// Auto-resets on pathname change so users can navigate away from a
// broken route without manually clicking Reload. The top-level
// <ErrorBoundary> still wraps the whole Router subtree as the final
// safety net.

import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

class RouteErrorBoundaryImpl extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Auto-reset when the pathname changes so the user can escape a
    // broken route just by clicking a sidebar link.
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  retry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message = this.state.error?.message || 'Unknown error';
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2.5, sm: 3 },
            maxWidth: 720,
            mx: 'auto',
            borderRadius: 2,
            borderLeft: 4,
            borderColor: 'error.main',
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ErrorIcon color="error" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                This page hit a snag
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              The rest of the app is still working. Try the page again, or
              use the sidebar to go somewhere else.
            </Typography>
            <Alert severity="error" variant="outlined">
              <AlertTitle>Error</AlertTitle>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-word' }}
              >
                {message}
              </Typography>
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.retry}
              >
                Try again
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => { window.location.assign('/dashboard'); }}
              >
                Back to dashboard
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }
}

const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <RouteErrorBoundaryImpl resetKey={location.pathname}>
      {children}
    </RouteErrorBoundaryImpl>
  );
};

export default RouteErrorBoundary;
